import { Account } from "models/Account";
import { Person } from "models/Person";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { useLocation, useNavigate } from "react-router-dom";
import { bindActionCreators } from "redux";
import { RootStateAccount } from "storeAccount/accountStore";
import { getInvoices } from "storeAccount/actions/Billing";
import { getAccountAssets } from "services/AccountApi";
import { CustomerState } from "storeAccount/reducers/Customers";
import { InvoiceState } from "storeAccount/reducers/Invoices";
import Logout from "./Utils/Logout";
import { useAccountTranslation } from "./translations";

interface Props {
    customerState: CustomerState;
    person: Person;
    customers: Account[];
    currentCustomer: Account | null;
    invoiceState: InvoiceState;
    onNavigate?: () => void;
}

interface Actions {
    setCustomer: (customer: Account) => void;
    getInvoices: () => void;
}

interface NavigationItem {
    path: string;
    label: string;
    icon: string;
    badge?: number;
    visible?: boolean;
}

const AccountMenu: React.FC<Props & Actions> = ({
    currentCustomer,
    customers,
    customerState,
    person,
    setCustomer,
    getInvoices,
    invoiceState,
    onNavigate
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useAccountTranslation();
    const [assetCount, setAssetCount] = useState(0);

    useEffect(() => {
        const refresh = () => {
            if (document.visibilityState === "visible") {
                getInvoices();
            }
        };
        document.addEventListener("visibilitychange", refresh);
        getInvoices();
        return () => document.removeEventListener("visibilitychange", refresh);
    }, [getInvoices]);

    useEffect(() => {
        let active = true;
        setAssetCount(0);
        if (!currentCustomer) return () => { active = false; };
        getAccountAssets(currentCustomer.id)
            .then(assets => { if (active) setAssetCount(assets.length); })
            .catch(() => { if (active) setAssetCount(0); });
        return () => { active = false; };
    }, [currentCustomer]);

    const selectCustomer = (id?: string) => {
        if (!id) return;
        const nextCustomer = customerState.getCustomerById(id);
        if (nextCustomer) {
            setCustomer(nextCustomer);
            navigate("/");
            onNavigate?.();
        }
    };

    const navigateTo = (path: string) => {
        navigate(path);
        onNavigate?.();
    };

    const customerInvoices = currentCustomer
        ? invoiceState.getInvoicesByAccountId(currentCustomer.id)
        : [];
    const invoices = customerInvoices.filter(invoice => !invoice.quote);
    const quotes = customerInvoices.filter(invoice => invoice.quote);
    const outstandingInvoices = invoices.filter(invoice =>
        invoice.customer === currentCustomer?.id && !invoice.IsPaid() && !invoice.draft
    );
    const navigation: NavigationItem[] = [
        { path: "/", label: t("overview"), icon: "mdi-home-variant-outline" },
        {
            path: "/invoices",
            label: t("invoices"),
            icon: "mdi-file-document-outline",
            badge: outstandingInvoices.length,
            visible: invoices.length > 0
        },
        {
            path: "/quotes",
            label: t("quotes"),
            icon: "mdi-file-sign",
            badge: quotes.length,
            visible: quotes.length > 0
        },
        { path: "/assets", label: t("assets"), icon: "mdi-cube-outline", visible: assetCount > 0 },
        { path: "/projects", label: t("projects"), icon: "mdi-briefcase-outline" },
        { path: "/my-details", label: t("myDetails"), icon: "mdi-account-cog-outline" }
    ];

    const active = (path: string) =>
        path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

    return <div className="account-sidebar-content">
        <div className="account-identity">
            <span className="account-person-avatar" aria-hidden="true">
                {(person.name || "?").trim().slice(0, 1).toUpperCase()}
            </span>
            <div>
                <span>{t("hello", { name: person.name })}</span>
                <small>{person.email}</small>
            </div>
        </div>

        <div className="account-company-picker">
            <label htmlFor="account-company-select">{t("chooseCompanyShort")}</label>
            <Select
                inputId="account-company-select"
                classNamePrefix="account-company-select"
                options={customers.map(customer => ({ value: customer.id, label: customer.legal_name }))}
                value={currentCustomer ? { value: currentCustomer.id, label: currentCustomer.legal_name } : null}
                onChange={option => selectCustomer(option?.value)}
                isSearchable={customers.length > 6}
            />
        </div>

        <nav className="account-nav" aria-label={t("menu")}>
            {navigation.filter(item => item.visible !== false).map(item =>
                <button
                    type="button"
                    key={item.path}
                    className={`account-nav-item${active(item.path) ? " account-nav-item--active" : ""}`}
                    aria-current={active(item.path) ? "page" : undefined}
                    onClick={() => navigateTo(item.path)}
                >
                    <span className="account-nav-icon"><i className={`mdi ${item.icon}`} aria-hidden="true" /></span>
                    <span className="account-nav-label">{item.label}</span>
                    {!!item.badge && <span className="account-nav-badge">{item.badge}</span>}
                </button>
            )}
        </nav>

        <div className="account-sidebar-footer">
            <Logout />
        </div>
    </div>;
};

const mapStateToProps = (state: RootStateAccount) => ({
    customerState: state.Customers,
    invoiceState: state.Invoices
});

const mapDispatchToProps = (dispatch: any) =>
    bindActionCreators({ getInvoices }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AccountMenu);
